import { onMounted, ref, toValue, watch } from "vue";
import { useElementBounding, useRafFn, useScreenOrientation } from "@vueuse/core";
import { toCssSizeUnit } from "@/common/helpers/style";
// Gap (px) between a flyout trigger and its menu
const FLYOUT_OFFSET_GAP = 12;
// Reading documentElement.clientWidth forces a full-document layout, and a long transcript mounts one
// dropdown per segment — so every dropdown created in the same task shares a single measurement.
const viewportSize = { width: ref(0), height: ref(0) };
let isListeningForResize = false;
let hasMeasuredThisTask = false;
const measureViewport = () => {
    viewportSize.width.value = document.documentElement.clientWidth;
    viewportSize.height.value = document.documentElement.clientHeight;
};
const useSharedViewport = () => {
    if (!isListeningForResize) {
        isListeningForResize = true;
        window.addEventListener("resize", measureViewport, { passive: true });
    }
    if (!hasMeasuredThisTask) {
        hasMeasuredThisTask = true;
        measureViewport();
        queueMicrotask(() => (hasMeasuredThisTask = false));
    }
    return viewportSize;
};
/**
 * Positions a floating menu against a reference element — the engine behind
 * DropdownMenu and AutoComplete. RTL-aware logical placement, flyout cascade,
 * auto above/below flip, viewport-padding clamping, and a per-frame trigger
 * follow while open. Returns the reactive style object to bind on the menu.
 */
export const useDropdownPosition = (referenceEl, menuEl, options) => {
    const isOpen = () => toValue(options.open);
    const isFlyout = () => toValue(options.isFlyout) ?? false;
    const isSelect = () => toValue(options.isSelect) ?? false;
    const isAbsolute = () => toValue(options.absolute) ?? false;
    const menuPosition = () => toValue(options.menuPosition) ?? "";
    const placement = () => toValue(options.placement) ?? "auto";
    const offsetY = () => toValue(options.offsetY) ?? 3;
    const menuWidthProp = () => toValue(options.menuWidth) ?? 0;
    const menuWidthMatchButton = () => toValue(options.menuWidthMatchButton) ?? false;
    const windowPadding = () => toValue(options.windowPadding) ?? 20;
    const menuMaxHeight = () => toValue(options.menuMaxHeight) ?? 0;
    const menuMinHeight = () => toValue(options.menuMinHeight) ?? 0;
    const align = () => toValue(options.align);
    const lockPositionOnOpen = () => toValue(options.lockPositionOnOpen) ?? false;
    const { update: updateButtonBounds, ...buttonBounds } = useElementBounding(referenceEl);
    const { update: updateMenuBounds, ...menuBounds } = useElementBounding(menuEl);
    const { width: windowWidth, height: windowHeight } = useSharedViewport();
    const lockedPosition = ref(null);
    const menuStyle = ref({
        top: undefined,
        bottom: undefined,
        left: undefined,
        right: undefined,
        maxHeight: undefined,
        minHeight: undefined,
        width: undefined,
        maxWidth: undefined
    });
    onMounted(() => {
        if (menuMaxHeight() > 0) {
            menuStyle.value.maxHeight = toCssSizeUnit(menuMaxHeight());
        }
        if (menuMinHeight() > 0) {
            menuStyle.value.minHeight = toCssSizeUnit(menuMinHeight());
        }
    });
    /**
     * Get button bounds, using locked position if available
     */
    const getButtonBounds = () => {
        if (lockPositionOnOpen() && lockedPosition.value && isOpen()) {
            return lockedPosition.value;
        }
        const bounds = {
            x: buttonBounds.x.value,
            width: buttonBounds.width.value,
            height: buttonBounds.height.value,
            right: buttonBounds.right.value,
            top: buttonBounds.top.value,
            bottom: buttonBounds.bottom.value
        };
        // Lock position when dropdown opens
        if (lockPositionOnOpen() && isOpen() && !lockedPosition.value) {
            lockedPosition.value = bounds;
        }
        return bounds;
    };
    /**
     * Apply menu width based on props
     */
    const applyMenuWidth = (buttonWidth) => {
        if (menuWidthProp() > 0) {
            menuStyle.value.width = toCssSizeUnit(menuWidthProp());
            menuStyle.value.maxWidth = toCssSizeUnit(menuWidthProp());
        }
        else if (isSelect() || (menuWidthMatchButton() && !!buttonWidth)) {
            menuStyle.value.width = toCssSizeUnit(buttonWidth);
            menuStyle.value.maxWidth = toCssSizeUnit(buttonWidth);
        }
    };
    /**
     * Calculate the base Y position
     */
    const calculateBaseY = (button, offsetTop) => {
        let y = button.bottom + offsetTop;
        if (isAbsolute()) {
            y = button.height + offsetY();
        }
        // Handle parent-top alignment for flyouts
        if (isFlyout() && align() === "parent-top") {
            const parentMenu = toValue(referenceEl)?.closest(".rec-dropdown-menu[role=menu]");
            if (parentMenu) {
                const { top } = parentMenu.getBoundingClientRect();
                y = top;
            }
        }
        return y;
    };
    /**
     * Position menu horizontally using logical properties (RTL-aware)
     */
    const positionHorizontally = (ctx, baseY, baseOffsetTop) => {
        const { button, menuWidth, isRtl, inlineStart, inlineEnd } = ctx;
        let y = baseY;
        let offsetTop = baseOffsetTop;
        const x = (isFlyout() ? button.right : button.x) + window.scrollX;
        // Check if menu would overflow on the inline-end side
        const wouldOverflowInlineEnd = isRtl
            ? button.right - menuWidth < windowPadding()
            : x + menuWidth > windowWidth.value;
        // menuPosition "left"/"right" are logical start/end: anchor the opposite edge to open toward that side (mirrors in RTL).
        const forceInlineEndAlign = menuPosition()?.includes("left");
        if (isFlyout()) {
            // A flyout cascades to the side of its trigger toward inline-end (physical right in LTR,
            // left in RTL); it flips to the inline-start side only when the natural side overflows.
            const inlineEndCascadeOverflows = isRtl
                ? button.x - menuWidth < windowPadding()
                : button.right + menuWidth > windowWidth.value;
            const inlineStartCascadeFits = isRtl
                ? button.right + menuWidth <= windowWidth.value - windowPadding()
                : button.x - menuWidth >= 0;
            if ((inlineEndCascadeOverflows || forceInlineEndAlign) && (inlineStartCascadeFits || isAbsolute())) {
                // Flip: menu's inline-end edge meets the trigger's inline-start edge
                menuStyle.value[inlineStart] = "";
                if (isAbsolute()) {
                    menuStyle.value[inlineEnd] = toCssSizeUnit(button.x + button.width - windowWidth.value);
                }
                else {
                    menuStyle.value[inlineEnd] = toCssSizeUnit(isRtl ? button.right : windowWidth.value - button.x);
                }
            }
            else if (inlineEndCascadeOverflows || forceInlineEndAlign) {
                // Neither side fits, fall back to window padding
                y = button.bottom;
                offsetTop = button.height;
                menuStyle.value[inlineEnd] = "";
                menuStyle.value[inlineStart] = toCssSizeUnit(windowPadding());
            }
            else {
                // Default: menu's inline-start edge meets the trigger's inline-end edge
                menuStyle.value[inlineEnd] = "";
                if (isAbsolute()) {
                    menuStyle.value[inlineStart] = "0";
                }
                else {
                    menuStyle.value[inlineStart] = toCssSizeUnit(isRtl ? windowWidth.value - button.x : x);
                }
            }
        }
        else if (wouldOverflowInlineEnd || forceInlineEndAlign) {
            // Non-flyout overflow: try aligning to inline-end instead
            const canFitInlineEnd = isRtl
                ? button.x + menuWidth <= windowWidth.value - windowPadding()
                : button.x + button.width - menuWidth >= 0;
            if (!canFitInlineEnd && !isAbsolute()) {
                // Menu cannot fit either way, fall back to window padding
                y = button.bottom + offsetTop;
                menuStyle.value[inlineEnd] = "";
                menuStyle.value[inlineStart] = toCssSizeUnit(windowPadding());
            }
            else {
                // Align menu's inline-end with button's inline-end
                menuStyle.value[inlineStart] = "";
                if (isAbsolute()) {
                    menuStyle.value[inlineEnd] = isRtl ? "0" : toCssSizeUnit(button.x + button.width - windowWidth.value);
                }
                else {
                    const pos = isRtl ? button.x : windowWidth.value - button.right;
                    menuStyle.value[inlineEnd] = toCssSizeUnit(pos);
                }
            }
        }
        else {
            // Default: align menu's inline-start with button's inline-start
            menuStyle.value[inlineEnd] = "";
            if (isAbsolute()) {
                menuStyle.value[inlineStart] = "0";
            }
            else {
                const pos = isRtl ? windowWidth.value - button.right : x;
                menuStyle.value[inlineStart] = toCssSizeUnit(pos);
            }
        }
        return { y, offsetTop };
    };
    /**
     * Position menu vertically (above or below button)
     */
    const positionVertically = (ctx, y, offsetTop) => {
        const { button, menuHeight } = ctx;
        let shouldPositionAbove;
        if (placement() === "above") {
            shouldPositionAbove = true;
        }
        else if (placement() === "below") {
            shouldPositionAbove = false;
        }
        else {
            // Auto placement - check if menu fits below button
            shouldPositionAbove = y + menuHeight > windowHeight.value - windowPadding();
        }
        if (shouldPositionAbove) {
            // Position above button
            menuStyle.value.top = toCssSizeUnit(Math.max(windowPadding(), button.top - offsetTop - menuHeight));
            if (menuHeight > windowHeight.value - 2 * windowPadding()) {
                menuStyle.value.maxHeight = toCssSizeUnit(windowHeight.value - 2 * windowPadding());
            }
        }
        else {
            // Position below button
            menuStyle.value.top = toCssSizeUnit(y);
            if (y + menuHeight > windowHeight.value - windowPadding()) {
                menuStyle.value.maxHeight = toCssSizeUnit(windowHeight.value - y - windowPadding());
            }
        }
    };
    /**
     * Main positioning orchestrator
     */
    const positionMenu = () => {
        if (!toValue(menuEl) || !toValue(referenceEl))
            return;
        // Force synchronous measurement of element bounds
        updateButtonBounds();
        updateMenuBounds();
        const button = getButtonBounds();
        if (!button)
            return;
        const menuWidth = menuBounds.width.value;
        const menuHeight = menuBounds.height.value;
        applyMenuWidth(button.width);
        // Detect RTL mode and define logical directions
        const isRtl = !!window.Ramius?.is_rtl;
        const inlineStart = isRtl ? "right" : "left";
        const inlineEnd = isRtl ? "left" : "right";
        const ctx = {
            button,
            menuWidth,
            menuHeight,
            isRtl,
            inlineStart,
            inlineEnd
        };
        const offsetTop = isFlyout() ? -button.height - FLYOUT_OFFSET_GAP : offsetY();
        const baseY = calculateBaseY(button, offsetTop);
        const { y, offsetTop: finalOffsetTop } = positionHorizontally(ctx, baseY, offsetTop);
        positionVertically(ctx, y, finalOffsetTop);
    };
    // Watch reactive sources that should trigger repositioning
    // flush: 'post' ensures DOM is updated before positioning runs
    watch([
        // all button bounds positionMenu reads: an in-place resize changes width/right without moving x/y
        buttonBounds.x,
        buttonBounds.y,
        buttonBounds.width,
        buttonBounds.height,
        buttonBounds.right,
        buttonBounds.top,
        buttonBounds.bottom,
        menuBounds.width,
        menuBounds.height,
        windowWidth,
        windowHeight,
        () => isOpen()
    ], positionMenu, { immediate: true, flush: "post" });
    // Follow the trigger each frame while open: useElementBounding misses position-only moves (RTL flip, reflow).
    const { pause: pauseTriggerFollow, resume: resumeTriggerFollow } = useRafFn(updateButtonBounds, { immediate: false });
    watch(() => isOpen(), (open) => (open && !lockPositionOnOpen() ? resumeTriggerFollow() : pauseTriggerFollow()), { immediate: true });
    // Clear locked position when dropdown closes
    watch(() => isOpen(), (open) => {
        if (!open && lockPositionOnOpen()) {
            lockedPosition.value = null;
        }
    });
    // Evaluate the style after an orientation change, with a delay to allow for animations to complete
    const { orientation } = useScreenOrientation();
    watch(orientation, () => {
        setTimeout(() => {
            if (!toValue(menuEl))
                return;
            positionMenu();
        }, 500);
    });
    return { menuStyle, positionMenu };
};
